import dayjs, { Dayjs } from 'dayjs'
import type { PrismicGeneralData } from '../interfaces/common/prismicGeneralConfig'

/**
 * ALIGN_START_DATE_WITH_SYLIUS="true" (Volcom only for now): count the lead
 * time like Sylius `DateTimeUtil::isFreeDay`, which always closes Sundays —
 * otherwise Sylius refuses the start date we send — and start automatically
 * created carts on a date the CMS lets customers pick.
 */
export const isStartDateAlignedWithSylius = () =>
  process.env.ALIGN_START_DATE_WITH_SYLIUS === 'true'

export const isNonBusinessDay = (
  date: Dayjs,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  /** When set from Prismic `disable_sunday_on_date_picker`, Sundays are non-business. */
  disableSundayFromCms?: boolean
): boolean => {
  const day = date.day()
  const isSunday = day === 0
  const isSaturday = day === 6
  const isHoliday = publicHolidays.has(date.format('YYYY-MM-DD'))
  return (
    (!!disableSundayFromCms && isSunday) ||
    (!isSaturdayABusinessDay && isSaturday) ||
    isHoliday
  )
}

export const getMinimumStartDate = (
  minimumStartingDays: number,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  disableSundayFromCms?: boolean
): Dayjs => {
  let startDate = dayjs()
  let businessDaysCount = 0

  while (businessDaysCount < minimumStartingDays) {
    startDate = startDate.add(1, 'day')
    if (
      !isNonBusinessDay(
        startDate,
        isSaturdayABusinessDay,
        publicHolidays,
        disableSundayFromCms
      ) &&
      !(isStartDateAlignedWithSylius() && startDate.day() === 0)
    ) {
      businessDaysCount += 1
    }
  }

  return startDate
}

/** CMS (general document) settings restricting which dates a customer can pick. */
export type CmsDateRules = Pick<
  PrismicGeneralData,
  | 'blocked_dates_start'
  | 'blocked_dates_end'
  | 'disable_saturday_on_date_picker'
  | 'disable_sunday_on_date_picker'
  | 'disable_monday_on_date_picker'
>

// A CMS blocked range can span months: this only guards against a config
// that closes every single day.
const MAX_DAYS_SEARCHED = 730

const isInCmsBlockedRange = (date: Dayjs, cms?: CmsDateRules | null) => {
  if (!cms?.blocked_dates_start || !cms?.blocked_dates_end) return false
  const start = dayjs(cms.blocked_dates_start)
  const end = dayjs(cms.blocked_dates_end)
  if (!start.isValid() || !end.isValid()) return false
  return !date.isBefore(start, 'day') && !date.isAfter(end, 'day')
}

/** Same rules as the date picker's `filterDate` + blocked range (`excludeDates`). */
export const isSelectableDate = (
  date: Dayjs,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  cms?: CmsDateRules | null
): boolean => {
  const day = date.day()
  if (
    isNonBusinessDay(
      date,
      isSaturdayABusinessDay,
      publicHolidays,
      !!cms?.disable_sunday_on_date_picker
    )
  ) {
    return false
  }
  if (day === 1 && cms?.disable_monday_on_date_picker) return false
  if (day === 6 && cms?.disable_saturday_on_date_picker) return false
  return !isInCmsBlockedRange(date, cms)
}

/** First date from `from` on that the customer could pick; `from` if none. */
export const getFirstSelectableDate = (
  from: Dayjs,
  isSaturdayABusinessDay: boolean | undefined,
  publicHolidays: Set<string>,
  cms?: CmsDateRules | null
): Dayjs => {
  let date = from
  for (let i = 0; i < MAX_DAYS_SEARCHED; i++) {
    if (isSelectableDate(date, isSaturdayABusinessDay, publicHolidays, cms)) {
      return date
    }
    date = date.add(1, 'day')
  }
  return from
}
